import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TruckIcon, UserIcon, BriefcaseIcon,
  HomeIcon, CalendarIcon,
  FileTextIcon, MapPinIcon, PackageIcon,
} from '../components/Icons';
import './Services.css';

const faqs = [
  {
    q: 'What is the SLA for the On-Demand delivery service?',
    a: 'Our On-Demand service operates on a 4-hour delivery SLA within Greater Accra. Same-day delivery is guaranteed for orders placed before 2pm.',
  },
  {
    q: 'Do you handle fragile or luxury valuables yourself?',
    a: 'Yes. We have a dedicated white-glove service for fragile, high-value, and luxury items with specialist packaging and a dedicated handler.',
  },
  {
    q: 'Can I learn more about national shipments?',
    a: 'Absolutely. Nationwide shipping covers all 16 regions of Ghana with 48–72 hour SLAs. Contact our team for volume pricing.',
  },
];

const services = [
  {
    Icon: TruckIcon,
    tag: 'Local Speed',
    title: 'Delivery',
    desc: 'Critical, same-day delivery service across Accra and surrounding suburbs. Speed redefined.',
  },
  {
    Icon: UserIcon,
    tag: 'Personal Care',
    title: 'Personal Errands',
    desc: 'We run your personal errands — pharmacy pickups, document drops, grocery runs — handled with discretion.',
  },
  {
    Icon: BriefcaseIcon,
    tag: 'Business Flow',
    title: 'Corporate Errands',
    desc: 'End-to-end corporate logistics support: inter-office deliveries, procurement pickups, and business dispatch.',
  },
  {
    Icon: HomeIcon,
    tag: 'Direct to You',
    title: 'Door to Door Delivery',
    desc: 'Seamless pickup from the sender and drop-off to the recipient — no third-party handoffs, no delays.',
  },
  {
    Icon: CalendarIcon,
    tag: 'Plan Ahead',
    title: 'Scheduled Delivery',
    desc: 'Set a recurring or future delivery time and we handle the rest, automatically and on schedule.',
  },
];

const Services: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <main className="services-page">
      {/* HERO */}
      <section className="services-hero services-hero--split">
        <div className="services-hero__panel services-hero__panel--light">
          <div className="services-hero__content">
            <p className="section-label">Premium Logistics</p>
            <h1 className="services-hero__title">Our Logistics Expertise</h1>
            <p className="services-hero__desc">
              We combine precision engineering with a global distribution network to provide seamless delivery solutions. From last-mile Accra deliveries to complex international freight, Droff is your kinetic partner.
            </p>
            <div className="services-hero__actions">
              <Link to="/contact" className="btn-primary">Request Quote</Link>
              <a href="#how" className="btn-outline-dark">Track Shipment</a>
            </div>
          </div>
        </div>
        <div className="services-hero__panel services-hero__panel--dark">
          <img
            className="svc-panel__bg"
            src="/services-hero.jpg"
            alt=""
            aria-hidden="true"
          />
          <div className="svc-panel__overlay" />
          <div className="svc-stats">
            {[
              { val: '500+', label: 'Daily Deliveries', accent: true },
              { val: '16',   label: 'Regions Covered',  accent: false },
              { val: '4hr',  label: 'On-Demand SLA',    accent: false },
              { val: '100%', label: 'Real-time Tracking', accent: true },
            ].map(({ val, label, accent }) => (
              <div key={label} className="svc-stat">
                <span className={`svc-stat__val${accent ? ' svc-stat__val--accent' : ''}`}>{val}</span>
                <span className="svc-stat__label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FULL SPECTRUM */}
      <section className="spectrum">
        <div className="container">
          <div className="spectrum__header">
            <p className="section-label">What We Do</p>
            <h2 className="section-title">A Full Spectrum of Mobility</h2>
            <p className="section-desc" style={{ marginTop: 12 }}>
              Tailored logistics services designed to scale with your business and personal requirements.
            </p>
          </div>
          <div className="spectrum__grid">
            {services.map(({ Icon, tag, title, desc }) => (
              <div key={title} className="spectrum-card">
                <div className="spectrum-card__icon"><Icon size={26} strokeWidth={1.6} /></div>
                <h3 className="spectrum-card__title">{title}</h3>
                <p className="spectrum-card__desc">{desc}</p>
                <span className="spectrum-card__tag">{tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="how-work" id="how">
        <div className="container">
          <div className="how-work__header">
            <h2 className="section-title">How We Work</h2>
          </div>
          <div className="how-work__steps">
            {[
              {
                Icon: FileTextIcon,
                step: '01',
                title: 'Request',
                desc: 'Input your parcel details and destination through our portal or app to get an instant quote.',
              },
              {
                Icon: MapPinIcon,
                step: '02',
                title: 'Pick Up',
                desc: 'Our rider arrives at your doorstep within the hour for local orders, ensuring professional handling.',
              },
              {
                Icon: PackageIcon,
                step: '03',
                title: 'Delivery',
                desc: 'Live tracking enables you to watch your cargo move in real-time until safe arrival at destination.',
              },
            ].map(({ Icon, step, title, desc }) => (
              <div key={step} className="how-step">
                <div className="how-step__icon-wrap"><Icon size={30} strokeWidth={1.6} /></div>
                <div className="how-step__num">{step}</div>
                <h3 className="how-step__title">{title}</h3>
                <p className="how-step__desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="knowledge-base">
        <div className="container">
          <h2 className="section-title knowledge-base__heading">Logistics Knowledge Base</h2>
          <div className="faq-list">
            {faqs.map((item, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' faq-item--open' : ''}`}>
                <button className="faq-item__q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <span className="faq-item__chevron">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="faq-item__a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--red)', padding: '72px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 'clamp(22px,3vw,34px)', fontWeight: 700, color: '#fff', maxWidth: 480 }}>
            Ready to experience kinetic precision?
          </h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ background: '#fff', color: 'var(--red)', borderColor: '#fff' }}>Get a Quote</Link>
            <Link to="/contact" className="btn-outline">Contact Sales</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
